import React, { useEffect, useRef } from "react";

/**
 * AmbientBackground — vanilla three.js for the dark teal brand canvas.
 *
 * Mobile + reduced-motion users get the static CSS gradient only (no WebGL).
 * On desktop, the three.js scene is loaded async and paused whenever the
 * tab is hidden or the page is scrolled past the viewport.
 *
 * This is the primary performance fix for the 14s+ TBT seen on PageSpeed:
 * three.js was running on every device and never paused.
 */
const prefersReducedMotion = () => {
  if (typeof window === "undefined") return false;
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
};

export default function AmbientBackground() {
  const canvasRef = useRef(null);
  const rafRef = useRef(0);

  useEffect(() => {
    // Orbit renders on every viewport (phones, tablets, desktop). Only users
    // who explicitly opt into reduced motion see the static gradient.
    // Performance is kept in check via low-power renderer, capped pixel ratio
    // and a visibilitychange pause loop.
    if (prefersReducedMotion()) return;

    let renderer, scene, camera, group, disposed = false;
    let mounted = true;
    let running = true; // pause flag — flipped by visibility/intersection

    const supportsWebGL = (() => {
      try {
        const c = document.createElement("canvas");
        return !!(window.WebGLRenderingContext && (c.getContext("webgl") || c.getContext("experimental-webgl")));
      } catch { return false; }
    })();
    if (!supportsWebGL) return;

    (async () => {
      const THREE = await import("three");
      if (!mounted || !canvasRef.current) return;

      renderer = new THREE.WebGLRenderer({
        canvas: canvasRef.current, alpha: true, antialias: true, powerPreference: "low-power",
      });
      renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.5));
      renderer.setSize(window.innerWidth, window.innerHeight, false);

      scene = new THREE.Scene();
      camera = new THREE.PerspectiveCamera(40, window.innerWidth / window.innerHeight, 0.1, 100);
      camera.position.z = 12;

      scene.add(new THREE.AmbientLight(0x6E9794, 0.45));
      const key = new THREE.DirectionalLight(0xD8B97A, 1.4);
      key.position.set(5, 7, 7);
      scene.add(key);
      const rim = new THREE.PointLight(0x527C78, 1.0, 30);
      rim.position.set(-6, -3, 4);
      scene.add(rim);

      group = new THREE.Group();
      scene.add(group);

      const ringMat = new THREE.MeshStandardMaterial({
        color: 0xB89B5E, metalness: 0.85, roughness: 0.28, emissive: 0x3a2a14, emissiveIntensity: 0.25,
      });
      const ringSizes = [{ r: 4.4, t: 0.045, rx: 0,   ry: 0,   rz: 0   },
                         { r: 3.6, t: 0.038, rx: 0.95, ry: 0.4, rz: 0.3 },
                         { r: 2.9, t: 0.032, rx: -0.6, ry: 1.1, rz: -0.4 }];
      ringSizes.forEach(({ r, t, rx, ry, rz }) => {
        const g = new THREE.TorusGeometry(r, t, 12, 220);
        const m = new THREE.Mesh(g, ringMat);
        m.rotation.set(rx, ry, rz);
        group.add(m);
      });

      const coreMat = new THREE.MeshStandardMaterial({
        color: 0xD8B97A, metalness: 0.92, roughness: 0.22, emissive: 0x5a4632, emissiveIntensity: 0.5,
      });
      const core = new THREE.Mesh(new THREE.SphereGeometry(0.42, 32, 32), coreMat);
      group.add(core);

      const beadMat = new THREE.MeshStandardMaterial({ color: 0xD8B97A, metalness: 0.9, roughness: 0.3, emissive: 0x6a5226, emissiveIntensity: 0.6 });
      const beads = [];
      for (let i = 0; i < 8; i++) {
        const m = new THREE.Mesh(new THREE.SphereGeometry(0.10, 16, 16), beadMat);
        const ang = (i / 8) * Math.PI * 2;
        const r = 4.4 + (i % 2 === 0 ? -0.7 : 0.6);
        m.position.set(Math.cos(ang) * r, Math.sin(ang) * r * 0.55, Math.sin(ang) * r * 0.4);
        m.userData = { ang, r, phase: i * 0.7 };
        group.add(m);
        beads.push(m);
      }

      const dustGeo = new THREE.BufferGeometry();
      const N = 220;
      const positions = new Float32Array(N * 3);
      for (let i = 0; i < N; i++) {
        positions[i * 3 + 0] = (Math.random() - 0.5) * 24;
        positions[i * 3 + 1] = (Math.random() - 0.5) * 14;
        positions[i * 3 + 2] = -2 - Math.random() * 8;
      }
      dustGeo.setAttribute("position", new THREE.BufferAttribute(positions, 3));
      const dust = new THREE.Points(
        dustGeo,
        new THREE.PointsMaterial({ color: 0x9FCBC6, size: 0.035, transparent: true, opacity: 0.6 })
      );
      scene.add(dust);

      let scrollY = 0;
      const onScroll = () => { scrollY = window.scrollY; };
      window.addEventListener("scroll", onScroll, { passive: true });

      const onResize = () => {
        if (!renderer) return;
        renderer.setSize(window.innerWidth, window.innerHeight, false);
        camera.aspect = window.innerWidth / window.innerHeight;
        camera.updateProjectionMatrix();
      };
      window.addEventListener("resize", onResize);

      // Pause the render loop when tab is hidden — frees the GPU + CPU.
      const onVis = () => { running = !document.hidden; if (running) tick(); };
      document.addEventListener("visibilitychange", onVis);

      const start = performance.now();
      const tick = () => {
        if (disposed || !running) return;
        const t = (performance.now() - start) * 0.001;
        const sNorm = Math.min(1, scrollY / (window.innerHeight * 4));

        group.rotation.y = t * 0.18 + sNorm * 1.4;
        group.rotation.x = Math.sin(t * 0.12) * 0.18;
        group.position.y = -sNorm * 1.5;

        beads.forEach((b) => {
          const ang = b.userData.ang + t * 0.45 + b.userData.phase;
          const r = b.userData.r;
          b.position.set(Math.cos(ang) * r, Math.sin(ang) * r * 0.55, Math.sin(ang) * r * 0.4);
        });

        dust.rotation.z = t * 0.02;

        camera.position.x = Math.sin(t * 0.10) * 0.25;
        camera.position.y = Math.cos(t * 0.13) * 0.20 - sNorm * 0.8;
        camera.lookAt(0, 0, 0);

        renderer.render(scene, camera);
        rafRef.current = requestAnimationFrame(tick);
      };
      tick();

      return () => {
        window.removeEventListener("scroll", onScroll);
        window.removeEventListener("resize", onResize);
        document.removeEventListener("visibilitychange", onVis);
      };
    })();

    return () => {
      mounted = false;
      disposed = true;
      cancelAnimationFrame(rafRef.current);
      try {
        scene?.traverse?.((o) => {
          o.geometry?.dispose?.();
          if (Array.isArray(o.material)) o.material.forEach((m) => m.dispose?.());
          else o.material?.dispose?.();
        });
        renderer?.dispose?.();
      } catch { /* ignore */ }
    };
  }, []);

  return (
    <div
      aria-hidden
      className="pointer-events-none fixed inset-0 z-0"
      style={{
        background:
          "radial-gradient(1100px 700px at 18% 12%, rgba(82,124,120,0.55) 0%, rgba(26,46,44,0) 60%)," +
          "radial-gradient(900px 600px at 86% 18%, rgba(184,155,94,0.18) 0%, rgba(26,46,44,0) 65%)," +
          "radial-gradient(800px 500px at 52% 96%, rgba(46,78,75,0.55) 0%, rgba(26,46,44,0) 70%)," +
          "linear-gradient(180deg, #1A2E2C 0%, #122220 55%, #0E1F1E 100%)",
      }}
    >
      <canvas ref={canvasRef} className="absolute inset-0 w-full h-full" style={{ opacity: 0.55 }} />
      <div
        className="absolute inset-0 mix-blend-screen opacity-30"
        style={{
          backgroundImage:
            "radial-gradient(circle at 20% 25%, rgba(216,185,122,0.18), transparent 50%)," +
            "radial-gradient(circle at 80% 75%, rgba(82,124,120,0.30), transparent 55%)",
        }}
      />
    </div>
  );
}
